import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REQUEST_TYPES = [
  { value: 'renewal', label: 'Renewal', icon: <RefreshCw className="h-4 w-4" />, desc: 'Extend your current service for another billing cycle' },
  { value: 'upgrade', label: 'Upgrade', icon: <TrendingUp className="h-4 w-4" />, desc: 'Switch to a higher-tier plan' },
  { value: 'downgrade', label: 'Downgrade', icon: <TrendingDown className="h-4 w-4" />, desc: 'Switch to a lower-tier plan' },
];

const BILLING_CYCLES = ['Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];

export function ServiceChangeRequestDialog({ open, onClose, order }) {
  const [requestType, setRequestType] = useState('renewal');
  const [billingCycle, setBillingCycle] = useState('Monthly');
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (open && requestType !== 'renewal') {
      fetchCategoriesAndProducts();
    }
  }, [open, requestType]);

  const fetchCategoriesAndProducts = async () => {
    setLoadingProducts(true);
    try {
      const [catRes, prodRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/client/products-with-pricing`),
      ]);
      setCategories(catRes.data || []);
      setAllProducts(prodRes.data || []);
    } catch { /* silently fail */ }
    finally { setLoadingProducts(false); }
  };

  const handleCategoryChange = (catId) => {
    setSelectedCategory(catId);
    setSelectedProduct(null);
    const cat = categories.find(c => c.id === catId);
    if (cat) {
      setProducts(allProducts.filter(p => p.category === cat.name));
    } else {
      setProducts(allProducts);
    }
  };

  const handleSubmit = async () => {
    if (requestType !== 'renewal' && !selectedProduct) {
      toast.error('Please select a product for upgrade/downgrade');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/service-change-requests`, {
        order_id: order.id,
        request_type: requestType,
        requested_product_id: selectedProduct?.id,
        requested_product_name: selectedProduct?.name,
        billing_cycle: requestType === 'renewal' ? billingCycle : null,
        new_price: selectedProduct ? (selectedProduct?.parent_prices?.monthly ?? selectedProduct?.base_price ?? 0) : (order.total_amount ?? order.amount ?? 0),
        note,
      });
      toast.success('Request submitted! Your partner will review it shortly.');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally { setLoading(false); }
  };

  return (<Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Service Change Request</DialogTitle>
        </DialogHeader>
        
        {/* Scrollable body section */}
        <div className="space-y-4 overflow-y-auto pr-1 flex-1">
          <div className="bg-[#2563eb]/10 rounded-lg p-3 text-sm">
            <p className="font-medium text-slate-800">{order?.product_name}</p>
            <p className="text-xs text-slate-500 mt-0.5">Current plan &mdash; ₹{(order?.total_amount ?? order?.amount ?? 0).toLocaleString()}</p>
          </div>

          {/* Request type selection */}
          <div className="grid grid-cols-3 gap-2 !mb-2">
            {REQUEST_TYPES.map(rt => (
              <button key={rt.value} type="button"
                onClick={() => { setRequestType(rt.value); setSelectedProduct(null); setSelectedCategory(''); }}
                className={`rounded-xl border-2 p-3 text-left transition-all ${requestType === rt.value ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid={`scr-type-${rt.value}`}>
                <div className="flex items-center gap-1.5 font-medium text-sm">{rt.icon}{rt.label}</div>
                <p className="text-xs text-slate-500 mt-1 leading-tight">{rt.desc}</p>
              </button>
            ))}
          </div>

          {/* Renewal: billing cycle selector */}
          {requestType === 'renewal' && (
            <div className="space-y-1.5">
              <Label>Billing Cycle</Label>
              <div className="grid grid-cols-4 gap-2">
                {BILLING_CYCLES.map(c => (
                  <button key={c} type="button"
                    onClick={() => setBillingCycle(c)}
                    className={`text-sm py-2 px-2 rounded-lg border ${billingCycle === c ? 'bg-blue-600 text-white border-blue-600' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Upgrade/Downgrade: product selection */}
          {requestType !== 'renewal' && (
            <div className="space-y-3">
              <div className="space-y-1.5 mb-2">
                <Label>Category</Label>
                <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger data-testid="scr-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selectedCategory && (
                <div className="space-y-1.5">
                  <Label>New Plan</Label>
                  {loadingProducts ? <div className="flex justify-center py-3"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {products.map(p => (
                        <button key={p.id} type="button"
                          onClick={() => setSelectedProduct(p)}
                          className={`mb-2 w-full text-left p-3 rounded-lg border text-sm transition-all ${selectedProduct?.id === p.id ? 'border-blue-600 bg-blue-50' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`scr-product-${p.id}`}>
                          <div className="flex justify-between items-center">
                            <span className="font-medium">{p.name}</span>
                            <span className="text-blue-600 font-semibold">₹{(p.parent_prices?.monthly ?? p.base_price ?? 0).toLocaleString()}</span>
                          </div>
                          {p.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{p.description}</p>}
                        </button>
                      ))}
                      {products.length === 0 && <p className="text-sm text-slate-400 text-center py-3">No products in this category</p>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Note */}
          <div className="space-y-1.5">
            <Label>Note (optional)</Label>
            <textarea value={note} onChange={e => setNote(e.target.value)}
              className="w-full border border-slate-200 rounded-md text-sm px-3 py-2 min-h-[72px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Any specific requirements or reason for this request..."
              data-testid="scr-note" />
          </div>
        </div>

        <DialogFooter className="mt-4 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="scr-submit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Submit Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
